import type { UITree } from "@/components/ai-render";
import { cn } from "@/lib/utils";
import { MemoRelation, MemoRelation_Type } from "@/types/proto/api/v1/memo_service_pb";
import { useTranslate } from "@/utils/i18n";
import JournalContent from "../../JournalContent";
import { JournalReactionListView } from "../../JournalReactionListView";
import { useJournalViewContext } from "../JournalViewContext";
import type { MemoBodyProps } from "../types";
import { AIAnalysisPreview } from "./AIAnalysisPreview";
import { AttachmentList, LocationDisplay, RelationList } from "./metadata";

interface MemoAIAnalysis {
  ui_tree?: UITree;
  analyzed_at?: string;
}

interface MemoWithAIAnalysis {
  aiAnalysis?: MemoAIAnalysis;
}

const JournalBody: React.FC<MemoBodyProps> = ({
  compact,
  onContentClick,
  onContentDoubleClick,
  onToggleNsfwVisibility,
  onAiPreviewClick,
}) => {
  const t = useTranslate();

  const { memo, parentPage, showNSFWContent, nsfw } = useJournalViewContext();

  const referencedMemos = memo.relations.filter((relation: MemoRelation) => relation.type === MemoRelation_Type.REFERENCE);

  // Extract AI analysis from memo (api-generated field)
  const aiAnalysis = (memo as unknown as MemoWithAIAnalysis).aiAnalysis;

  return (
    <>
      <div
        className={cn(
          "w-full flex flex-col justify-start items-start gap-2",
          nsfw && !showNSFWContent && "blur-lg transition-all duration-200",
        )}
      >
        <JournalContent
          key={`${memo.name}-${memo.updateTime}`}
          content={memo.content}
          onClick={onContentClick}
          onDoubleClick={onContentDoubleClick}
          compact={memo.pinned ? false : compact} // Always show full content when pinned
        />
        <AttachmentList attachments={memo.attachments} />
        <RelationList relations={referencedMemos} currentMemoName={memo.name} parentPage={parentPage} />
        {memo.location && <LocationDisplay location={memo.location} />}
        <JournalReactionListView memo={memo} reactions={memo.reactions} />

        {/* AI Analysis Preview - only show in compact mode and when data is available */}
        {compact && aiAnalysis && <AIAnalysisPreview aiAnalysis={aiAnalysis} compact={compact} onClick={onAiPreviewClick} />}
      </div>

      {/* NSFW content overlay */}
      {nsfw && !showNSFWContent && (
        <>
          <div className="absolute inset-0 bg-transparent" />
          <button
            type="button"
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 py-2 px-4 text-sm text-muted-foreground hover:text-foreground hover:bg-accent hover:border-accent border border-border rounded-lg bg-card transition-colors"
            onClick={onToggleNsfwVisibility}
          >
            {t("memo.click-to-show-nsfw-content")}
          </button>
        </>
      )}
    </>
  );
};

export default JournalBody;
